import { useState, useEffect } from "react";

import axios from "axios";

import { getErrorMessage, checkInputErrors } from '@/utils/Errors';

import { getModalStyle } from '@/utils/Modal';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

import Button from '@/components/Button';
import Dropdown from '@/components/Dropdown';
import Input from '@/components/Input';
import InputFile from '@/components/InputFile';

import {
    Save as SaveIcon,
    Close as CancelIcon,
    LocationPin as LocationIcon,
} from '@mui/icons-material';

import { getFormData } from '@/utils/Format';
import { formatFilesToSend } from '@/utils/Columns';

export default function ResourceMaintenanceModal({t, open, setOpen, fullScreen, resource, setChanged, user, token, apiUrl, 
    language, sendMessage, handleLogin, users, config, openUrlModal}){

	const { sxDialogMinHeight } = getModalStyle();

    const handleClose = () => {
        setOpen(false);
    }

    const [ errors, setErrors ] = useState(null);
    const [ data, setData ] = useState({
        responsible: null,
        reason: '',
        files: null
    });

    const resetData = () => {
        setData({
            responsible: null,
            reason: '',
            files: null
        });
    }

    useEffect(() => {
        if(open){
            resetData();
            setErrors(null);
        }
    },[open]);

    const handleInput = (e) => {
        
        var name = e.target.name;
        var value = e.target.value;

        var newData = {...data};
        newData[name] = value;
        setData(newData);
    };

    // to manage the dropdowns
    const handleResponsible = (newItem) => {
        var newData = {...data};
        newData.responsible = newItem;
        setData(newData);
    }

    const handleSubmit = () => {
        
        let fieldsNotEmpty = ['responsible'];

        // if entering maintenance, reason is required
        if(!resource?.situation?.maintenance){
            fieldsNotEmpty.push('reason');
        }

        setErrors(null);
        const errorsCheck = checkInputErrors(data,fieldsNotEmpty);

        if(Object.keys(errorsCheck).length === 0){

            // if on maintenance, do the opposite
            let apiPath;
            if(resource?.situation?.maintenance){
                apiPath = '/resource_rental/maintenance/remove';
            }
            else{
                apiPath = '/resource_rental/maintenance/set';
            }

            const configApi = {
                headers: { Authorization: `Bearer ${token}` } 
            };            

            const params = {
                language: language,
                companyuser: user?.company,
                resource: resource?.id,
                responsible: data?.responsible?.id,
                reason: data?.reason,
            };

            // if rented, send plan id to backend
            if(resource?.situation?.rented){
                params.plan = resource?.situation?.event?.plan?.id;
            }

            params.files = formatFilesToSend(data.files);

            const send = getFormData(null,params,null,['files']);

            var errorMessage = t('resource_rentals.maintenance.error');
            
            const url = apiUrl+apiPath;
            
            axios.post(url,send,configApi)
            .then(function (response) {

                if(response?.data){
              
                    const responseData = response.data;                

                    // if result is ok (1)
                    if(responseData?.result){
                        // the opposite
                        const status = resource?.situation?.maintenance ? 'out' : 'in';
                        sendMessage(t('resource_rentals.maintenance.'+status+'.status'),resource?.situation?.maintenance ? 'success' : 'success');
                        
                        if(responseData?.data?.reload){
                            window.location.reload();
                        }
                        else{           
                            const newResource = responseData?.data?.resource;
                            setChanged(newResource);
                            setOpen(false);
                        }
                    } 
                    else{
                        if(responseData?.status && responseData.status === 401){
                            handleLogin();                        
                        }
                        else{
                            // check error message
                            if(responseData?.data?.error){
                                
                                errorMessage = t('resource_rentals.maintenance.errors.'+responseData?.data?.error);                                

                                // if error and resource, refresh the data
                                if(responseData?.data?.resource){
                                    setChanged(responseData?.data?.resource);
                                    setOpen(false);
                                }
                            }
                            
                            sendMessage(errorMessage,'error');
                        }                        
                    }
                }
                else{                
                    sendMessage(errorMessage,'error');                    
                }
            })
            .catch(function (error){
                
                if(error?.status && error.status === 401){
                    handleLogin();                        
                }
                else{                        
                    sendMessage(errorMessage,'error');
                }
            });
        }
        else{            
            const errorMessage = getErrorMessage(t,'resource_rentals.maintenance',errorsCheck);
            setErrors(errorsCheck);
            sendMessage(errorMessage,'error');
        }
    }

    return (
        
        <Dialog
            container={fullScreen?.active ? fullScreen?.node?.current : null}
            sx={sxDialogMinHeight}
            onClose={handleClose}
            open={open}
            fullWidth={true}            
        >                        
        	<DialogTitle className='modal-title'>
                {resource?.name}
            </DialogTitle>            
            <DialogContent className='modal-content text-center'>
                <div className='mb-4'>
                    {
                        resource?.situation?.maintenance ? 
                            t('resource_rentals.maintenance.out.title')
                        :
                            t('resource_rentals.maintenance.in.title')
                    }                    
                </div>
                <div className='mb-4'>
                {
                    !resource?.situation?.maintenance &&
                    <div className='border app-border-color-gradient rounded p-2 app-bg-gradient'>
                        <div className='border-b app-border-color-gradient text-center mb-2 font-bold'>
                            {t('resource_rentals.maintenance.location')}
                        </div>
                        <div className=''>
                            <LocationIcon/>
                            {
                                resource?.situation?.rented ? 
                                    resource?.situation?.location && resource?.situation?.location?.length > 0 ?
                                        <a href={resource?.situation?.location} target='_blank'>{resource?.situation?.event?.plan?.name}</a>
                                    :
                                        resource?.situation?.event?.plan?.name                                    
                                :
                                    t('resource_rentals.maintenance.campa')
                            }
                        </div>
                    </div>                    
                }
                </div>
                <div className='mb-4'>
                    <Dropdown                    
                        type='search'
                        name='responsible'
                        value={data.responsible}
                        placeholder={t('resource_rentals.maintenance.fields.responsible')}
                        options={users}
                        handleDropdown={handleResponsible}
                        error={errors && errors?.responsible ? t('resource_rentals.maintenance.errors.'+errors.responsible) : ''}
                    />
                </div>
                <div className='mb-4'>
                    <Input                      
                        name="reason"
                        type="multiline"
                        rows={6}
                        value={data.reason}                      
                        placeholder={t('resource_rentals.maintenance.fields.reason')}
                        onChange={handleInput}
                        error={errors && errors?.reason ? t('resource_rentals.maintenance.errors.'+errors.reason) : ''}                        
                    />
                </div>
                <div className='mb-4'>                    
                    <InputFile
                        t={t}
                        table='resource_rentals'
                        field='files'
                        files={data.files}
                        data={data}
                        setData={setData}
                        openUrlModal={openUrlModal}
                        config={config}
                        errors={errors}
                        editable={true}
                        size='small'
                    />
                </div>
            </DialogContent>            
            <div className="modal-buttons">
                <div className='mr-1'>
                    <SaveIcon className='action-button-modal text-green-800' onClick={handleSubmit}/>
                </div>                                
                <div className='mr-1'>
                    <CancelIcon className='action-button-modal text-red-800' onClick={handleClose}/>
                </div>
            </div>        
        </Dialog>
  );
}